import { Col, Input, Row, Select, Typography } from 'antd';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ErrorMessage from '../../../components/Form/ErrorMessage';
import FormContent from '../../../components/Form/FormContent';
import FormHeaderLabel from '../../../components/Form/HeaderLabel';
import AlumlyButton from '../../../components/AlumlyButton';
import { NotificationEnum } from '../../../enums/notification';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import { ValidationRuleEnum } from '../../../enums/validationRule';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';

type CreateFormT = {
  title: string;
  message: string;
  targetUserTypes: string[];
};

const TARGET_USER_TYPE_OPTIONS = [
  { label: 'นักศึกษา', value: 'bba_student' },
  { label: 'ศิษย์เก่า', value: 'alumni' },
  { label: 'อาจารย์', value: 'faculty' },
  { label: 'Employer', value: 'employer' },
];

const FormCreateNotification: React.FC = () => {
  const navigate = useNavigate();
  const {
    control, handleSubmit, formState: { errors }, watch,
  } = useForm({
    defaultValues: {
      title: '',
      message: '',
      targetUserTypes: [] as string[],
    },
  });

  const { service: createNotificationService } = useServices(ServiceNameEnum.notification.create);

  const onSubmit = async (formData: CreateFormT): Promise<void> => {
    const { title, message, targetUserTypes } = formData;
    const { data, error } = await createNotificationService.post({
      header: title,
      body: message,
      direction: NotificationEnum.direction.home.value,
      sentTo: NotificationEnum.sentToType.all.value,
      type: NotificationEnum.type.promotion,
      targetUserTypes,
    });
    if (data) {
      toast.success('Create Notification Success');
      navigate(RouteNameEnum.notification.index);
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      if (errorResponse) {
        toast.error(errorResponse?.message);
      }
    }
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div style={{ marginTop: 32 }}>
        <FormHeaderLabel label="General info" />
        <FormContent>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={16}>
              <Typography.Text>ส่งถึง</Typography.Text>
              <Controller
                name="targetUserTypes"
                control={control}
                rules={{
                  validate: (value) => (value && value.length > 0) || 'กรุณาเลือกกลุ่มผู้รับอย่างน้อย 1 กลุ่ม',
                }}
                render={({ field, fieldState }) => (
                  <Select
                    mode="multiple"
                    size="large"
                    placeholder="เลือกกลุ่มผู้รับ"
                    options={TARGET_USER_TYPE_OPTIONS}
                    status={fieldState.error ? 'error' : ''}
                    style={{ width: '100%' }}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
              {errors.targetUserTypes && (
                <ErrorMessage>
                  {errors.targetUserTypes?.message}
                </ErrorMessage>
              )}
            </Col>
            <Col xs={24} md={16}>
              <Typography.Text>Title</Typography.Text>
              <Controller
                name="title"
                control={control}
                rules={{
                  required: ValidationRuleEnum.notification.required.message,
                  maxLength: ValidationRuleEnum.notification.title.maxLength,
                }}
                render={({ field, fieldState }) => (
                  <Input
                    size="large"
                    placeholder="Enter notification title"
                    status={fieldState.error ? 'error' : ''}
                    {...field}
                  />
                )}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {errors.title ? (
                  <ErrorMessage>
                    { errors.title?.message }
                  </ErrorMessage>
                ) : (
                  <Typography.Text type="secondary">
                    {`Limit to ${ValidationRuleEnum.notification.title.maxLength.value} characters`}
                  </Typography.Text>

                )}
                <Typography.Text type="secondary">
                  {`(${watch('title').length}/${ValidationRuleEnum.notification.title.maxLength.value})`}
                </Typography.Text>
              </div>
            </Col>
            <Col xs={24} md={16}>
              <Typography.Text>Message</Typography.Text>
              <Controller
                name="message"
                control={control}
                rules={{
                  required: ValidationRuleEnum.notification.required.message,
                  maxLength: ValidationRuleEnum.notification.message.maxLength,
                }}
                render={({ field, fieldState }) => (
                  <Input.TextArea
                    placeholder="Enter notification message"
                    rows={3}
                    status={fieldState.error ? 'error' : ''}
                    {...field}
                  />
                )}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {errors.message ? (
                  <ErrorMessage>
                    { errors.message?.message }
                  </ErrorMessage>
                ) : (
                  <Typography.Text type="secondary">
                    {`Limit to ${ValidationRuleEnum.notification.message.maxLength.value} characters`}
                  </Typography.Text>

                )}
                <Typography.Text type="secondary">
                  {`(${watch('message').length}/${ValidationRuleEnum.notification.message.maxLength.value})`}
                </Typography.Text>
              </div>
            </Col>
          </Row>
        </FormContent>
      </div>
      <Row gutter={[16, 16]} justify="center" style={{ marginTop: 16 }}>
        <Col>
          <AlumlyButton label="white" width={120} onClick={() => navigate(RouteNameEnum.notification.index)}>
            Cancel
          </AlumlyButton>
        </Col>
        <Col>
          <AlumlyButton type="submit" width={120}>Create</AlumlyButton>
        </Col>
      </Row>
    </form>
  );
};

export default FormCreateNotification;
